
import React, { useState } from "react";
import {
  Typography,
  Row,
  Col,
  Button,
  Tooltip,
  Input,
  Table,
  TableColumnsType,
} from "antd";
import { Container, RowInfo, TitleContainer } from "./elements";
import { useQuery } from "@apollo/client";
import { GET_NOTIFICATIONS } from "./requests";
import {
  PlusOutlined,
  EditOutlined,
  SendOutlined,
} from "@ant-design/icons";
import CreateNotification from "./components/modals/create-modal";
import EditNotification from "./components/modals/edit-modal";
import SendNotification from "./components/modals/send-modal";
import moment from "moment-timezone";
import { notificationTypes } from "../../types";

const { Title, Text } = Typography;

const Notifications: React.FC = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showSendModal, setShowSendModal] = useState<boolean>(false);
  const [selectedNotification, setSelectedNotification] =
    useState<typeof notificationTypes.notification | null>(null);
  const [searchTitle, setSearchTitle] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1,
    pageSize: 10 });

  const {
    data: notificationsData,
    refetch: refetchNotifications,
    loading: loadingNotifications,
  } = useQuery(GET_NOTIFICATIONS, {
    variables: {
      params: pageConfig,
      title: searchTitle,
    },
  });

  const allNotifications: typeof notificationTypes.notification[] =
    notificationsData?.notifications?.results || [];

  const columns: TableColumnsType<typeof notificationTypes.notification> = [
    {
      title: "Nombre",
      align: "left",
      key: "title",
      render: (row: typeof notificationTypes.notification) => (
        <RowInfo>
          <Title level={5}>{row.title}</Title>
          <p>{row.description}</p>
        </RowInfo>
      ),
    },
    {
      title: "Fecha Agendada",
      align: "center",
      key: "scheduledDate",
      render: (row: typeof notificationTypes.notification) => {
        const scheduledDate = row?.scheduledDate
          ? moment(row?.scheduledDate).format("D MMMM YYYY, HH:mm")
          : "N/A";
        return (
          <RowInfo>
            <Title level={5}>{scheduledDate}</Title>
          </RowInfo>
        );
      },
    },
    {
      title: "Fecha de Creación",
      align: "center",
      key: "createdAt",
      render: (row: typeof notificationTypes.notification) => (
        <RowInfo>
          <Title level={5}>
            {moment(row?.createdAt).format("D MMM YYYY")}
          </Title>
        </RowInfo>
      ),
    },
    {
      width: "10%",
      title: () => (
        <Title
          level={4}
          style={{ marginTop: "auto",
            marginBottom: "auto" }}
        >
          Acciones
        </Title>
      ),
      key: "action",
      render: (row: typeof notificationTypes.notification) => {
        const isPastDate = moment(row.scheduledDate).isBefore(moment());
        return (
          <Row>
            <Tooltip title="Editar">
              <Button
                type="primary"
                icon={<EditOutlined />}
                shape="circle"
                size="large"
                style={{ margin: 5 }}
                onClick={() => {
                  setSelectedNotification(row);
                  setShowEditModal(true);
                }}
              />
            </Tooltip>
            <Tooltip title={isPastDate ? "Ya se Envió" : "Mandar notificación"}>
              <Button
                icon={<SendOutlined style={{ color: "white" }} />}
                shape="circle"
                size="large"
                style={{ margin: 5,
                  backgroundColor: "#52B266" }}
                disabled={isPastDate}
                onClick={() => {
                  setSelectedNotification(row);
                  setShowSendModal(true);
                }}
              />
            </Tooltip>
          </Row>
        );
      },
    },
  ];

  return (
    <Container>
      <TitleContainer>
        <Col>
          <Input
            style={{ marginRight: 10 }}
            placeholder="Buscar por Nombre"
            value={searchTitle}
            onChange={(e) => setSearchTitle(e.target.value)}
            allowClear
          />
        </Col>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setShowModal(true);
          }}
          style={{ marginLeft: 20 }}
        >
            Crear Notificación
        </Button>
      </TitleContainer>
      <div style={{ marginTop: 20 }}>
        <Table
          loading={loadingNotifications}
          locale={{ emptyText: "No hay notificaciones" }}
          dataSource={allNotifications}
          columns={columns}
          rowKey="id"
          pagination={{
            current: Number(pageConfig.page),
            defaultCurrent: pageConfig.page,
            pageSize: Number(pageConfig.pageSize),
            defaultPageSize: pageConfig.pageSize,
            total: notificationsData?.notifications?.info?.count,
            showTotal: (total) => (
              <Text disabled>{`${total} resultados`}</Text>
            ),
            onChange: (page, pageSize) => {
              setPageConfig({
                page,
                pageSize,
              });
            },
            showSizeChanger: true,
          }}
        />
      </div>
      <CreateNotification
        showModal={showModal}
        setShowModal={setShowModal}
        refetch={refetchNotifications}
      />
      <EditNotification
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        selectedNotification={selectedNotification}
        setSelectedNotification={setSelectedNotification}
        refetch={refetchNotifications}
      />
      <SendNotification
        showModal={showSendModal}
        setShowModal={setShowSendModal}
        selectedNotification={selectedNotification}
        setSelectedNotification={setSelectedNotification}
        refetch={refetchNotifications}
      />
    </Container>
  );
};

export default Notifications;
